import React, { createContext, useContext, ReactNode, useMemo } from 'react';

import { ApiEndpoints, buildApiEndpoints, getBackendConfig } from 'ts-core';

/**
 * @deprecated Use hostName prop directly instead. Environment enum is no longer needed.
 * Backend URL is now configured via BEAT_API_BASE_URL environment variable.
 */
export enum Environment {
  PRODUCTION = 'prodApi',
  PRODUCTION2 = 'prodApi2',
  DEVELOPMENT = 'devApi',
  DEVELOPMENT2 = 'devApi2',
}

interface ApiInfo {
  apiEndpoints: ApiEndpoints;
  apiKey?: string;
}

const ApiContext = createContext<ApiInfo | undefined>(undefined);

type ApiProviderProps = {
  children: ReactNode;
  hostName?: string;
  /** Override base URL for file upload endpoints (bypasses CDN). Falls back to hostName. */
  fileUploadHostName?: string;
  production?: boolean;
  environment?: Environment;
  apiKey?: string;
  initializeRemoteConfig?: boolean;
};

export const ApiProvider: React.FC<ApiProviderProps> = ({
  children,
  production, // deprecated
  environment, // deprecated
  apiKey,
  hostName,
  fileUploadHostName,
  initializeRemoteConfig: _initializeRemoteConfig = true, // keeping for backward compatibility
}) => {
  // If hostName not explicitly provided, use environment variable
  if (!hostName) {
    try {
      const config = getBackendConfig();
      hostName = config.baseUrl;

      // Use config apiKey if not explicitly provided
      if (!apiKey) {
        apiKey = config.apiKey;
      }

      // Use config fileUploadBaseUrl if not explicitly provided
      if (!fileUploadHostName) {
        fileUploadHostName = config.fileUploadBaseUrl;
      }
    } catch (error) {
      // If getBackendConfig fails, throw a helpful error
      throw new Error(
        'ApiProvider requires backend configuration. Either:\n' +
          '1. Set BEAT_API_BASE_URL and BEAT_API_KEY environment variables, or\n' +
          '2. Pass hostName and apiKey props directly\n\n' +
          `Original error: ${
            error instanceof Error ? error.message : String(error)
          }`
      );
    }
  }

  // Fall back to hostName if fileUploadHostName was never resolved
  const effectiveFileUploadHost = fileUploadHostName || hostName;

  const apiEndpoints = useMemo(
    () => buildApiEndpoints(hostName!, effectiveFileUploadHost),
    [hostName, effectiveFileUploadHost]
  );

  const contextValue = useMemo(() => ({ apiEndpoints, apiKey }), [apiEndpoints, apiKey]);

  return (
    <ApiContext.Provider value={contextValue}>
      {children}
    </ApiContext.Provider>
  );
};

export function useApi(): ApiInfo {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within a ApiProvider');
  }
  return context;
}

/**
 * Get the current auth token from localStorage
 */
function getAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('token');
}

/**
 * Centralized fetch utility that automatically includes Authorization header
 * when user is logged in.
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options (method, headers, body, etc.)
 * @param apiKey - Optional API key to include as x-partner-key header
 * @returns Promise<Response>
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {},
  apiKey?: string
): Promise<Response> {
  const token = getAuthToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  // Add Authorization header if user is logged in
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add API key if provided
  if (apiKey) {
    headers['x-partner-key'] = apiKey;
  }

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Hook that returns apiFetch with apiKey pre-configured
 */
export function useApiFetch() {
  const { apiKey } = useApi();
  
  return (url: string, options: RequestInit = {}): Promise<Response> => {
    return apiFetch(url, options, apiKey);
  };
}
